import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'

// Views - Lazy loading para otimização
const Login = () => import('../views/Login.vue')
const ForgotPassword = () => import('../views/ForgotPassword.vue')
const ResetPassword = () => import('../views/ResetPassword.vue')
const PrivacyPolicy = () => import('../views/PrivacyPolicy.vue')
const TermsOfUse = () => import('../views/TermsOfUse.vue')
const Dashboard = () => import('../views/Dashboard.vue')
const Users = () => import('../views/Users.vue')
const Establishments = () => import('../views/Establishments.vue')
const Services = () => import('../views/Services.vue')
const Clients = () => import('../views/Clients.vue')
const Appointments = () => import('../views/Appointments.vue')
const Financeiro = () => import('../views/Financeiro.vue')
const Relacionamento = () => import('../views/Relacionamento.vue')
const MyCommissions = () => import('../views/MyCommissions.vue')
const MySales = () => import('../views/MySales.vue')
const MyServices = () => import('../views/MyServices.vue')
const Ajuda = () => import('../views/Ajuda.vue')
const PublicBooking = () => import('../views/PublicBooking.vue')
const Vendas = () => import('../views/Vendas.vue')
const Configuracoes = () => import('../views/Configuracoes.vue')
const PlatformDashboard = () => import('../views/PlatformDashboard.vue')
const PlatformMapa = () => import('../views/PlatformMapa.vue')
const PlatformPessoas = () => import('../views/PlatformPessoas.vue')
const PlatformCobranca = () => import('../views/PlatformCobranca.vue')
const PlatformFinanceiro = () => import('../views/PlatformFinanceiro.vue')
const PlatformConfiguracoes = () => import('../views/PlatformConfiguracoes.vue')
const PlatformLegal = () => import('../views/PlatformLegal.vue')
const PlatformBlog = () => import('../views/PlatformBlog.vue')
const PlatformSolicitacoes = () => import('../views/PlatformSolicitacoes.vue')
const Novidades = () => import('../views/Novidades.vue')
const PortalPlans = () => import('../views/portal/PortalPlans.vue')
const PortalSubscription = () => import('../views/portal/PortalSubscription.vue')
const PortalNotificacoes = () => import('../views/portal/PortalNotificacoes.vue')
const PortalPackages = () => import('../views/portal/PortalPackages.vue')
const PortalPackageDetail = () => import('../views/portal/PortalPackageDetail.vue')

// Rota inicial correta por role — o dono da plataforma não tem acesso ao
// Dashboard operacional (é por estabelecimento), então precisa do próprio
// "home" pra evitar loop de redirecionamento no guard abaixo. Cliente final
// (role CLIENT) nunca vê o Dashboard administrativo — o "home" dele é a
// vitrine de planos do portal.
export function homeRouteName(role) {
  if (role === 'PLATFORM_OWNER') return 'PlatformDashboard'
  if (role === 'CLIENT') return 'PortalPlans'
  return 'Dashboard'
}

const routes = [
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true, requiresRole: ['ADMIN', 'RECEPTIONIST', 'PROFESSIONAL'] }
  },
  {
    path: '/plataforma',
    name: 'PlatformDashboard',
    component: PlatformDashboard,
    meta: { requiresAuth: true, requiresRole: ['PLATFORM_OWNER'] }
  },
  {
    path: '/plataforma/mapa',
    name: 'PlatformMapa',
    component: PlatformMapa,
    meta: { requiresAuth: true, requiresRole: ['PLATFORM_OWNER'] }
  },
  {
    path: '/plataforma/pessoas',
    name: 'PlatformPessoas',
    component: PlatformPessoas,
    meta: { requiresAuth: true, requiresRole: ['PLATFORM_OWNER'] }
  },
  {
    path: '/plataforma/cobranca',
    name: 'PlatformCobranca',
    component: PlatformCobranca,
    meta: { requiresAuth: true, requiresRole: ['PLATFORM_OWNER'] }
  },
  {
    path: '/plataforma/financeiro',
    name: 'PlatformFinanceiro',
    component: PlatformFinanceiro,
    meta: { requiresAuth: true, requiresRole: ['PLATFORM_OWNER'] }
  },
  {
    path: '/plataforma/configuracoes',
    name: 'PlatformConfiguracoes',
    component: PlatformConfiguracoes,
    meta: { requiresAuth: true, requiresRole: ['PLATFORM_OWNER'] }
  },
  {
    path: '/plataforma/legal',
    name: 'PlatformLegal',
    component: PlatformLegal,
    meta: { requiresAuth: true, requiresRole: ['PLATFORM_OWNER'] }
  },
  {
    path: '/plataforma/solicitacoes',
    name: 'PlatformSolicitacoes',
    component: PlatformSolicitacoes,
    meta: { requiresAuth: true, requiresRole: ['PLATFORM_OWNER'] }
  },
  {
    path: '/plataforma/blog',
    name: 'PlatformBlog',
    component: PlatformBlog,
    meta: { requiresAuth: true, requiresRole: ['PLATFORM_OWNER'] }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false }
  },
  {
    path: '/esqueci-senha',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresAuth: false }
  },
  {
    path: '/redefinir-senha',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresAuth: false }
  },
  {
    path: '/privacidade',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy,
    meta: { requiresAuth: false }
  },
  {
    path: '/termos',
    name: 'TermsOfUse',
    component: TermsOfUse,
    meta: { requiresAuth: false }
  },
  {
    path: '/establishments',
    name: 'Establishments',
    component: Establishments,
    meta: { requiresAuth: true, requiresRole: ['ADMIN', 'RECEPTIONIST'], requiresFeature: 'ESTABLISHMENTS' }
  },
  {
    path: '/appointments',
    name: 'Appointments',
    component: Appointments,
    meta: { requiresAuth: true, requiresRole: ['ADMIN', 'RECEPTIONIST', 'PROFESSIONAL'], requiresFeature: 'APPOINTMENTS' }
  },
  {
    path: '/clients',
    name: 'Clients',
    component: Clients,
    meta: { requiresAuth: true, requiresRole: ['ADMIN', 'RECEPTIONIST'], requiresFeature: 'CLIENTS' }
  },
  {
    path: '/services',
    name: 'Services',
    component: Services,
    meta: { requiresAuth: true, requiresRole: ['ADMIN', 'RECEPTIONIST'], requiresFeature: 'SERVICES' }
  },
  {
    // Profissionais são usuários com perfil PROFESSIONAL — cadastro em Configurações.
    path: '/professionals',
    redirect: '/configuracoes',
  },
  {
    path: '/financial',
    name: 'Financial',
    component: Financeiro,
    meta: { requiresAuth: true, requiresRole: ['ADMIN'], requiresFeature: 'FINANCIAL' }
  },
  {
    path: '/vendas',
    name: 'Vendas',
    component: Vendas,
    meta: { requiresAuth: true, requiresRole: ['ADMIN', 'RECEPTIONIST'], requiresFeature: 'VENDAS' }
  },
  {
    // Pacotes virou aba dentro de Serviços — mantém o link antigo funcionando.
    path: '/pacotes',
    redirect: '/services',
  },
  {
    path: '/relacionamento',
    name: 'Relacionamento',
    component: Relacionamento,
    meta: { requiresAuth: true, requiresRole: ['ADMIN', 'RECEPTIONIST'], requiresFeature: ['CLIENT_SUBSCRIPTIONS', 'COUPONS', 'LOYALTY', 'COMMISSIONS'] }
  },
  {
    // Visão do profissional: só as próprias comissões (GET /commissions/me).
    path: '/minhas-comissoes',
    name: 'MyCommissions',
    component: MyCommissions,
    meta: { requiresAuth: true, requiresRole: ['PROFESSIONAL'], requiresFeature: 'COMMISSIONS' }
  },
  {
    // Visão do profissional: só as próprias vendas (GET /sales já filtra no
    // servidor). Depende da permissão que o admin liga em Configurações.
    path: '/minhas-vendas',
    name: 'MySales',
    component: MySales,
    meta: { requiresAuth: true, requiresRole: ['PROFESSIONAL'], requiresFeature: 'VENDAS', requiresPermission: 'sales.view_own' }
  },
  {
    path: '/meus-servicos',
    name: 'MyServices',
    component: MyServices,
    meta: { requiresAuth: true, requiresRole: ['PROFESSIONAL'], requiresFeature: 'SERVICES', requiresPermission: 'services.edit_own' }
  },
  {
    path: '/ajuda',
    name: 'Ajuda',
    component: Ajuda,
    meta: { requiresAuth: true, requiresRole: ['ADMIN', 'RECEPTIONIST', 'PROFESSIONAL'] }
  },
  {
    path: '/agendar/:slug',
    name: 'PublicBooking',
    component: PublicBooking,
    meta: { requiresAuth: false }
  },
  {
    path: '/configuracoes',
    name: 'Configuracoes',
    component: Configuracoes,
    meta: { requiresAuth: true, requiresRole: ['ADMIN'] }
  },
  {
    path: '/novidades',
    name: 'Novidades',
    component: Novidades,
    meta: { requiresAuth: true, requiresRole: ['PLATFORM_OWNER'] }
  },
  {
    path: '/portal',
    name: 'PortalPlans',
    component: PortalPlans,
    meta: { requiresAuth: true, requiresRole: ['CLIENT'] }
  },
  {
    path: '/portal/assinatura',
    name: 'PortalSubscription',
    component: PortalSubscription,
    meta: { requiresAuth: true, requiresRole: ['CLIENT'] }
  },
  {
    path: '/portal/pacotes',
    name: 'PortalPackages',
    component: PortalPackages,
    meta: { requiresAuth: true, requiresRole: ['CLIENT'], requiresFeature: 'PACOTES' }
  },
  {
    path: '/portal/pacotes/:id',
    name: 'PortalPackageDetail',
    component: PortalPackageDetail,
    meta: { requiresAuth: true, requiresRole: ['CLIENT'], requiresFeature: 'PACOTES' }
  },
  {
    path: '/portal/notificacoes',
    name: 'PortalNotificacoes',
    component: PortalNotificacoes,
    meta: { requiresAuth: true, requiresRole: ['CLIENT'] }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Guards de navegação
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (to.meta.requiresAuth !== false) {
    if (!authStore.isAuthenticated) {
      return next({ name: 'Login' })
    }

    const role = authStore.user?.role

    const allowedRoles = to.meta.requiresRole

    if (allowedRoles && !allowedRoles.includes(role)) {
      return next({ name: homeRouteName(role) })
    }

    // Permissão configurável pelo admin (só afeta PROFESSIONAL; os demais
    // perfis passam, ver authStore.can).
    if (to.meta.requiresPermission && !authStore.can(to.meta.requiresPermission)) {
      return next({ name: homeRouteName(role) })
    }

    if (to.meta.requiresFeature) {
      // Array = grupo de sub-funcionalidades (ex: Relacionamento), libera
      // com pelo menos uma; string = checagem única, como antes.
      const hasAccess = Array.isArray(to.meta.requiresFeature)
        ? authStore.hasAnyFeature(to.meta.requiresFeature)
        : authStore.hasFeature(to.meta.requiresFeature)
      if (!hasAccess) return next({ name: homeRouteName(role) })
    }

    // Assinatura cancelada, ou plano vinculado ainda sem o primeiro pagamento:
    // só Configurações (Pagamentos/Legal) fica acessível pra ADMIN reativar/
    // pagar — os demais perfis STAFF não têm essa tela, então ficam presos no
    // Dashboard (que exibe o aviso de bloqueio em vez do conteúdo normal, ver
    // Dashboard.vue). CLIENT fica de fora dessa regra de propósito: o cliente
    // final da barbearia não deve perder acesso ao próprio portal por causa de
    // um atraso no pagamento da mensalidade da barbearia com a plataforma —
    // além disso, CLIENT não tem rota 'Dashboard', então cairia em loop.
    if (authStore.subscriptionBlocked && role !== 'CLIENT') {
      if (role === 'ADMIN' && to.name !== 'Configuracoes') {
        return next({ name: 'Configuracoes', query: { tab: 'pagamentos' } })
      }
      if (role !== 'ADMIN' && to.name !== 'Dashboard') {
        return next({ name: 'Dashboard' })
      }
    }
  }

  // Se já autenticado e tenta acessar login, redireciona para home correta
  if (to.name === 'Login' && authStore.isAuthenticated) {
    return next({ name: homeRouteName(authStore.user?.role) })
  }

  next()
})

export default router